// react and external-libs
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ViewSurveyButton from './ViewSurveyButton';
import moment from 'moment';
// MUI
import { Box, Button, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
//redux
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { resetSurvey, fetchSurvey as fetchSurveyPreload } from '../../app/store/surveySlice';
import { fetchWorkflowByIdSlice } from '../../app/store/workflowSlice';
import { beginAssessment } from '../../services/workflowThunks';
import {
  fetchAssessment as fetchAssessmentPreload,
  resetAssessment,
  updateAssessmentArrayAssessmentSurvey,
} from '../../app/store/assessmentSlice';
// interface
import User  from '../../interfaces/users.interface';
import { Company, CompanyApplication, Contact, Version } from '../../interfaces/users.interface';
import { Survey } from '../../interfaces/survey.interface';
import { Workflow } from '../../interfaces/workflow.interface';
import { Assessment, AssessmentSurvey } from '../../interfaces/assessment.interface';
// service
import { PRE_ASSESSMENT_STATUSES, WORKFLOW_STATUSES, WorkflowStatus } from '../../services/workflow';
import {
  assessmentActionLabel,
  SURVEY_ACTIONS_LABELS,
  surveyActionLabel,
  surveyDisplayLogic,
  surveyStatusColor,
  testSubmitAssessmentButton,
} from '../../services/surveyHelpers';
import {
  fetchAssessment,
} from '../../services/assessments';
// components
import AssessmentForm from '../Assessments/NewAssessmentModal';
import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import WorkflowDownloadsMenuButton from './WorkflowDownloadsMenuButton';
import { USER_TYPES } from '../../services/user';
import { eligibleForBlueBadge } from '../../services/badge';
import { AsyncSaveButton } from '../../components/buttons/AsyncSaveButton';

type WorkflowButtonsProps = {
  company: Company;
  workflow?: Workflow;
  survey?: Survey;
  assessmentSurvey?: AssessmentSurvey;
  site?: Contact;
  companyApplication?: CompanyApplication;
  showCanBeginNewWorkflow?: boolean;
  setCanStartNewQuestionniare? : (canStartNewQuestionniare: boolean) => void;
  version?: Version;
  setAssessmentNotifications?: React.Dispatch<React.SetStateAction<any[]>>;
};

const WorkflowButtons = (props: WorkflowButtonsProps) => {
  // props
  const { company, workflow, survey, assessmentSurvey, site, showCanBeginNewWorkflow = false, setAssessmentNotifications, setCanStartNewQuestionniare } = props;
  // redux
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { results: questionnaires } = useAppSelector(state => state.questionnaires);
  const { assessment } = useAppSelector(state => state.assessments);
  const { survey: cSurvey } = useAppSelector((state) => state.surveys);
  // state
  const [preAssessmentErrorOpen, setPreAssessmentErrorOpen] = useState(false);
  const [cAssessment, setCAssessment] = useState<Assessment>();
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [beginAssessmentOpen, setBeginAssessmentOpen] = useState(false);
  const [isBeginningAssessment, setIsBeginningAssessment] = useState(false);
  const [visitAssessment, setVisitAssessment] = useState<{ assessment: number; survey: number } | undefined>(undefined);
  // short/simple functions
  const handleAssessmentOpen = useCallback(() => setAssessmentModalOpen(true), []);
  const handleAssessmentClose = useCallback(() => setAssessmentModalOpen(false), []);
  const handleBeginAssessmentClose = useCallback(() => setBeginAssessmentOpen(false), []);

  const handleClearPreLoad = useCallback(() => {
    dispatch(resetAssessment());
    dispatch(resetSurvey());
  }, [dispatch]);

  const handleBeginAssessmentOpen = useCallback(async () => {
    if (!workflow) return;

    try {
      setIsBeginningAssessment(true);

      const blueBadgeEligibility = await eligibleForBlueBadge(workflow.id!);
      if (blueBadgeEligibility && 'error' in blueBadgeEligibility) {
        setPreAssessmentErrorOpen(true);
        return;
      }

      handleClearPreLoad();
      setBeginAssessmentOpen(true);
    } catch (error) {
      console.error('Error: ', error);
    } finally {
      dispatch(fetchWorkflowByIdSlice(workflow.id));
      setIsBeginningAssessment(false);
    }
  }, [workflow]);

  const linkHandler = useCallback(
    (id: number, e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      handleClearPreLoad();
      navigate(`/questionnaire/${id}/`);
    },
    [navigate],
  );

  // Preload survey and assessment for Questionnaire page
  const handlePreLoad = useCallback(() => {
    if (workflow?.bpSurvey && workflow.bpSurvey !== cSurvey?.id) {
      dispatch(fetchSurveyPreload(workflow.bpSurvey));
    }
    if (workflow?.assessment && workflow.assessment !== assessment?.id) {
      dispatch(fetchAssessmentPreload(workflow.assessment));
    }
  }, [workflow, dispatch, cSurvey, assessment]);

  const handleVisitAssessment = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      if (!workflow?.assessmentSurvey || !workflow?.bpSurvey) return;
      setTimeout(() => {
        setVisitAssessment({
          assessment: workflow?.assessment as number,
          survey: workflow.bpSurvey as number,
        });
      }, 500);
    },
    [workflow],
  );

  const updateAssessmentSurveyStatus = useCallback(
    async () => {
      if (!workflow?.assessmentSurvey || !workflow?.bpSurvey) return;
      try {
        const result = await dispatch(beginAssessment(workflow.id)).unwrap();
        dispatch(updateAssessmentArrayAssessmentSurvey(result.assessmentSurvey));
        handleVisitAssessment();
      } catch (error) {
        console.error('Failed to begin assessment:', error);
      }
      handleBeginAssessmentClose();
    },
    [dispatch, workflow, handleVisitAssessment],
  );

  // navigating to assessment link
  useEffect(() => {
    if (!visitAssessment) return;
    navigate(`/assessment/${visitAssessment.assessment}/questionnaire/${visitAssessment.survey}/`);

    return () => {
      setVisitAssessment(undefined);
    };
  }, [visitAssessment, navigate]);

  const surveyStatusDisplay = React.useMemo(() => surveyDisplayLogic({
    survey,
    companyCerts: company.companyCertifications,
    workflow,
  }), [survey, company, workflow]);

  const surveyType = React.useMemo(() => workflow?.bpSurvey
    ? 'TPN Best Practices Questionnaire'
    : workflow?.site
      ? 'Site Scoping Baseline'
      : 'Application Scoping Baseline', [workflow]);

  const surveyActLabel = React.useMemo(() => {
    const label = surveyActionLabel(
      surveyType,
      typeof surveyStatusDisplay === 'string' ? surveyStatusDisplay : '',
      Boolean(workflow?.baseline && !workflow?.bpSurvey),
    );
    const isAdmin = user?.type === USER_TYPES.VENDOR_ADMIN || user?.type === USER_TYPES.TPN_ADMIN;
    return label === SURVEY_ACTIONS_LABELS.scheduleAssessment && !isAdmin ? SURVEY_ACTIONS_LABELS.viewQuestionnaire : label;
  }, [workflow, surveyStatusDisplay]);

  // fetching assessment data for revert and AssessmentNotificationsAlertIcon
  useEffect(() => {
    if (workflow?.assessment && workflow?.assessmentSurvey) {
      fetchAssessment(workflow.assessment, { excludeAssessorAnswers: true }).then(workflowAssessment => {
        setCAssessment(workflowAssessment);
        if (setAssessmentNotifications) setAssessmentNotifications(workflowAssessment.notifications);
      });
    }
  }, [workflow?.status, workflow?.assessment, workflow?.assessmentSurvey]);

  const assessmentActionObj = useMemo(() => assessmentActionLabel(workflow?.status, user), [workflow?.status, user]);
  const { label: actionLabel, icon: actionIcon } = assessmentActionObj;

  const shouldShowViewSurveyButton = (wf: Workflow, usr: User, label: string) => {
    if (!wf) return false;

    const isBaselineOnlyIncomplete = !wf.bpSurvey && wf.baseline && wf.status === WORKFLOW_STATUSES.INCOMPLETE;
    const isContentOwnerOrAdmin = usr?.type === USER_TYPES.TPN_ADMIN || usr?.type === USER_TYPES.CONTENT_OWNER;
    const isSurveyPendingOrIncomplete = surveyStatusDisplay === 'Pending' && wf.bpSurvey && wf.status === 'incomplete';
    const isSurveyActionLabelMatch = surveyActLabel === SURVEY_ACTIONS_LABELS.continueQuestionnaire ||
      label === 'View and Comment' ||
      label === 'Update Assessment';
    const isNonAdminAndScheduleAssessment = usr?.type !== USER_TYPES.TPN_ADMIN &&
      (typeof surveyStatusDisplay === 'string' && surveyActLabel === SURVEY_ACTIONS_LABELS.scheduleAssessment);
    const isCompleteWithoutAssessmentSurvey = wf.status.toLowerCase() === 'complete' && !wf.assessmentSurvey;

    return isBaselineOnlyIncomplete || isContentOwnerOrAdmin || isSurveyPendingOrIncomplete || isSurveyActionLabelMatch || isNonAdminAndScheduleAssessment || isCompleteWithoutAssessmentSurvey;
  };

  const showReassignButton: boolean = useMemo(() => {
    return workflow?.status === WORKFLOW_STATUSES.REJECTED &&
    company.admins?.some(admin => admin.id === user?.id) &&
    user?.type === USER_TYPES.VENDOR_ADMIN &&
    !!cAssessment;
  }, [workflow, company, user, cAssessment]);

  const isSurveyArchived = useMemo(() => {
    if (!survey) return false;

    const questionnaireId =
      typeof survey.questionnaire === 'number'
        ? survey.questionnaire
        : survey.questionnaire?.id;

    if (!questionnaireId) return false;

    const q = questionnaires.find((x) => x.id === questionnaireId);
    if (!q?.archiveDate) return false;

    return moment(q.archiveDate).isBefore(moment());
  }, [survey, questionnaires]);

  const canSeeActionButton = useMemo(() => {
    return (
      !isSurveyArchived &&
      !!workflow?.isActive &&
      (user?.type === USER_TYPES.VENDOR_ADMIN ||
        (user?.type === USER_TYPES.ASSESSOR &&
          workflow?.status === WORKFLOW_STATUSES.SUBMITTED))
    );
  }, [isSurveyArchived, workflow?.isActive, workflow?.status, user?.type]);

  return (
    <>
      <StandardDialog
          title="Cannot Begin Assessment"
          handleClose={() => setPreAssessmentErrorOpen(false)}
          isOpen={preAssessmentErrorOpen}
        >
        <Typography>
          The Service Provider Questionnaire is incomplete. Please contact the
          Service Provider and ask them to complete the Questionnaire.
        </Typography>
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => setPreAssessmentErrorOpen(false)}>
            OK
          </Button>
        </StandardDialogActions>
      </StandardDialog>
      <Box sx={{ position: 'relative', maxWidth: '100%', width: '205px',  minWidth: '170px' }}>
        {!workflow && user?.type === USER_TYPES.VENDOR_ADMIN && (
          <Button variant='outlined' startIcon={<Add />}>
            New Questionnaire
          </Button>
        )}
        {(!workflow || !workflow?.status) && user?.type !== USER_TYPES.VENDOR_ADMIN && (
          <Button variant='outlined'>Click for details</Button>
        )}
        {workflow && (
          <>
            {user?.type === USER_TYPES.CONTENT_OWNER &&
              !workflow?.assessmentSurvey &&
              workflow?.bpSurvey &&
              workflow?.status !== WORKFLOW_STATUSES.SUBMITTED_FOR_ASSESSMENT &&
              workflow?.baseline && (
                <ViewSurveyButton workflow={workflow} limit='Baseline' actionLabel={actionLabel} actionIcon={actionIcon}/>
            )}
            {(user?.type !== USER_TYPES.CONTENT_OWNER ||
              workflow?.assessmentSurvey ||
              (workflow.status === WORKFLOW_STATUSES.SUBMITTED_FOR_ASSESSMENT || workflow.status === WORKFLOW_STATUSES.REJECTED)) && (
              <>
                {workflow?.assessmentSurvey && actionLabel !== 'Update Assessment' && actionLabel !== 'View and Comment' ? (
                  <Stack spacing={1} alignItems='center'>
                    {user?.type !== USER_TYPES.CONTENT_OWNER &&
                      (user?.type !== USER_TYPES.ASSESSOR ||
                        ['Complete', 'Completed', 'Approved', 'Remediation'].indexOf(workflow?.status) === -1) && (
                          <ViewSurveyButton
                            setCanStartNewQuestionniare={setCanStartNewQuestionniare}
                            showCanBeginNewWorkflow={showCanBeginNewWorkflow}
                            handlePreLoad={handlePreLoad}
                            handleVisitAssessment={handleVisitAssessment}
                            workflow={workflow}
                            actionIcon={actionIcon}
                            actionLabel={
                              testSubmitAssessmentButton(assessmentSurvey, user?.type)
                                ? SURVEY_ACTIONS_LABELS.reviewAndSubmit
                                : assessmentActionLabel(workflow?.status, user).label
                            }/>
                    )}
                    {user?.type === USER_TYPES.ASSESSOR && workflow && PRE_ASSESSMENT_STATUSES.includes(workflow.status as WorkflowStatus) && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '205px',
                        maxWidth: '100%',
                        minWidth: '170px',
                      }}
                      >
                        <AsyncSaveButton
                          color="primary"
                          isSaving={isBeginningAssessment}
                          savingLabel="Saving..."
                          label={'Begin Assessment'}
                          fullWidth
                          size='small'
                          variant='outlined'
                          disabled={isBeginningAssessment}
                          onClick={handleBeginAssessmentOpen}
                        />
                      </Box>
                    )}
                    {workflow?.status &&
                      ['Complete', 'Completed', 'Remediation'].indexOf(workflow?.status) === -1 &&
                      user?.type === USER_TYPES.CONTENT_OWNER && (
                        <ViewSurveyButton workflow={workflow} actionIcon={<DynamicFormIcon fontSize="small"/>} actionLabel={SURVEY_ACTIONS_LABELS.viewQuestionnaire}/>
                    )}
                    {workflow?.status &&
                      ['Complete', 'Completed', 'Remediation'].indexOf(workflow?.status) > -1 &&
                      user?.type === USER_TYPES.CONTENT_OWNER && (
                        <ViewSurveyButton workflow={workflow} handlePreLoad={handlePreLoad} handleVisitAssessment={handleVisitAssessment} actionIcon={<DynamicFormIcon fontSize="small"/>} actionLabel={SURVEY_ACTIONS_LABELS.viewQuestionnaire}/>
                    )}
                    { showReassignButton && (
                      <>
                        <Button
                          sx={{ maxWidth: '100%', width: '205px', minWidth: '170px' }}
                          size='small'
                          color={surveyStatusColor(survey)}
                          variant='outlined'
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssessmentModalOpen(true);
                          }}
                        >REASSIGN</Button>
                      </>
                    )}
                  </Stack>
                ) : (
                  <>
                    { user && shouldShowViewSurveyButton(workflow, user, actionLabel) && (
                      <Box sx={{ mb: 1 }}>
                      {/* Primary Workflow access button */}
                      <ViewSurveyButton
                        limit={
                        ((user?.type === USER_TYPES.VENDOR_ADMIN || user?.type === USER_TYPES.ASSESSOR) && workflow.status === WORKFLOW_STATUSES.SUBMITTED) ||
                        (surveyStatusDisplay === SURVEY_ACTIONS_LABELS.pending && user?.type === USER_TYPES.TPN_ADMIN) || (actionLabel === 'Update Assessment' && workflow.status !== WORKFLOW_STATUSES.PRE_ASSESSMENT) ? 'Baseline' : undefined}
                        workflow={workflow}
                        actionIcon={<DynamicFormIcon
                        fontSize="small"/>}
                        setCanStartNewQuestionniare={setCanStartNewQuestionniare}
                        showCanBeginNewWorkflow={showCanBeginNewWorkflow}
                        handleVisitAssessment={user?.type === USER_TYPES.VENDOR_ADMIN && actionLabel === 'Update Assessment' && workflow.status === WORKFLOW_STATUSES.PRE_ASSESSMENT ? handleVisitAssessment : undefined}
                        actionLabel={SURVEY_ACTIONS_LABELS.viewQuestionnaire}
                        />
                      </Box>
                    )}
                    { canSeeActionButton && (
                      <Button
                        sx={{ maxWidth: '100%', width: '205px', minWidth: '170px' }}
                        size='small'
                        color={surveyStatusColor(survey)}
                        variant='outlined'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearPreLoad();
                          if (
                            typeof surveyStatusDisplay === 'string' &&
                            surveyActLabel === SURVEY_ACTIONS_LABELS.scheduleAssessment
                          ) {
                            handleAssessmentOpen();
                          } else {
                            if (actionLabel === SURVEY_ACTIONS_LABELS.updateAssessment || actionLabel === 'View and Comment') {
                              handleVisitAssessment();
                            } else {
                              linkHandler((workflow?.bpSurvey || workflow?.baseline) as number);
                            }
                          }
                        }}
                      >
                      {actionLabel ===  SURVEY_ACTIONS_LABELS.updateAssessment || actionLabel === 'View and Comment' ? actionLabel : surveyActLabel}
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
            <Box sx={{ mt: 1 }}>
              <WorkflowDownloadsMenuButton workflow={workflow} company={company} site={site} />
            </Box>
          </>
        )}
      </Box>
      <AssessmentForm modalOpen={assessmentModalOpen} company={company} closeCallback={handleAssessmentClose} assessment={cAssessment} />
      <StandardDialog title={'Begin Assessment'} handleClose={handleBeginAssessmentClose} isOpen={beginAssessmentOpen}>
        <Typography>Are you sure you want to begin this assessment? Once you begin, the Service Provider can no longer update their answers.</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={handleBeginAssessmentClose}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() => updateAssessmentSurveyStatus()}
            disabled={!user || user.type !== USER_TYPES.ASSESSOR}
          >
            Begin
          </Button>
        </StandardDialogActions>
      </StandardDialog>
    </>
  );
};

export default WorkflowButtons;
